// Team health for the pilot (TEAM-12, TEAM-13, D-104, D-108): is each person's phone app working, and are their calls reaching
// Voxowa? What the page says about one person, and the team's totals.
import { phoneReadiness } from "./readiness";
import type { HealthMember } from "./types";

export type PhoneState = "on_call" | "active" | "quiet" | "not_today" | "no_phone";

const MINUTE = 60_000;

/** The last sign of life from the phone: its last sync or visit, a call that reached Voxowa, a call under way. */
export function lastSeen(m: HealthMember): Date | null {
  const times = [
    m.device?.last_seen_at,
    m.device?.last_sync_at,
    m.calls.last_synced_at,
    m.live?.updated_at,
  ].filter((t): t is string => !!t).map((t) => new Date(t).getTime());
  return times.length ? new Date(Math.max(...times)) : null;
}

/** When the phone last sent calls to Voxowa. A sync often stops before its final report, because Android freezes the
 * app while she is on her next call, so the report's time alone runs hours behind the calls. */
export function callsSentAt(m: HealthMember): string | null {
  const times = [m.calls.last_synced_at, m.device?.last_sync_at].filter((t): t is string => !!t);
  return times.length ? times.reduce((a, b) => (new Date(a) > new Date(b) ? a : b)) : null;
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function ago(at: Date | string | null, now: Date): string {
  if (!at) return "never";
  const minutes = Math.floor((now.getTime() - new Date(at).getTime()) / MINUTE);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** Whether the phone is on a call, active in the last 15 minutes, seen earlier today, or not since. */
export function phoneState(m: HealthMember, now: Date, dayStart: Date): { state: PhoneState; text: string } {
  if (!m.device) return { state: "no_phone", text: "No phone set up" };
  if (m.live) return { state: "on_call", text: "On a call" };
  const seen = lastSeen(m);
  if (seen && now.getTime() - seen.getTime() <= 15 * MINUTE) return { state: "active", text: "Active" };
  if (seen && seen >= dayStart) return { state: "quiet", text: `Seen ${ago(seen, now)}` };
  return { state: "not_today", text: seen ? `Not seen today (${ago(seen, now)})` : "Never seen" };
}

/** What may be stopping this person from using the app, in words for the owner. */
export function healthProblems(
  m: HealthMember,
  plan: { coach: boolean; autopilot: boolean } | undefined,
): string[] {
  const problems: string[] = [];
  if (m.role === "caller" && !m.device) problems.push("Has not signed in on a phone yet");
  if (!m.device) return problems;
  if (!m.notice_accepted) problems.push("Has not accepted the notice in the app, so nothing is sent");
  if (m.signins_today >= 3) {
    problems.push(`Signed in ${m.signins_today} times today: the app may be signing them out`);
  }
  if (m.device.sync_problem) problems.push(m.device.sync_problem);
  const readiness = phoneReadiness(plan, m.device.permissions);
  problems.push(...readiness.problems, ...readiness.advice);
  if ((m.device.files_waiting ?? 0) >= 20) problems.push(`${m.device.files_waiting} recordings waiting to be sent`);
  problems.push(...recordingProblems(m));
  problems.push(...popupProblems(m));
  return problems;
}

/** Answered calls with no recording (TEAM-14, D-118). Files still waiting on the phone may be some of them; three or
 * more beyond those means the phone's own call recorder is missing calls (its setting, or the recorder itself). */
export function recordingProblems(m: HealthMember): string[] {
  const unrecorded = m.calls.unrecorded ?? 0;
  if (unrecorded - (m.device?.files_waiting ?? 0) < 3) return [];
  return [
    `${unrecorded} of ${
      m.calls.answered_10s ?? unrecorded
    } answered calls today have no recording: check the phone's call recording`,
  ];
}

/** The pop-ups not doing their job (TEAM-13): the ringing one missing on incoming calls, or the after-call one opened
 * again and again with hardly a choice made on it. */
export function popupProblems(m: HealthMember): string[] {
  const problems: string[] = [];
  const r = m.ringing;
  if (r && r.incoming >= 3 && r.with_popup === 0) {
    problems.push(`No ringing pop-up on any of ${r.incoming} incoming calls today`);
  } else if (r && r.incoming >= 5 && r.with_popup * 2 < r.incoming) {
    problems.push(`Ringing pop-up on only ${r.with_popup} of ${r.incoming} incoming calls today`);
  }
  const a = m.after_call;
  if (a && a.shown_today >= 10 && a.choices * 5 < a.shown_today) {
    problems.push(
      a.choices === 0
        ? `No choice made on any of ${a.shown_today} after-call pop-ups today`
        : `A choice on only ${a.choices} of ${a.shown_today} after-call pop-ups today`,
    );
  }
  return problems;
}

export interface HealthTotals {
  people: number;
  withPhone: number;
  active: number;
  last15: number;
  lastHour: number;
  today: number;
  outgoing: number;
  incoming: number;
  missed: number;
  connected: number;
  choices: number;
  answered: number;
  unrecorded: number;
  signinsToday: number;
  withProblems: number;
  popupsShown: number;
  popupChoices: number;
  ringingIncoming: number;
  ringingWithPopup: number;
}

export function healthTotals(
  members: HealthMember[],
  plan: { coach: boolean; autopilot: boolean } | undefined,
  now: Date,
  dayStart: Date,
): HealthTotals {
  const sum = (f: (m: HealthMember) => number) => members.reduce((n, m) => n + f(m), 0);
  return {
    people: members.length,
    withPhone: members.filter((m) => m.device).length,
    active: members.filter((m) => ["active", "on_call"].includes(phoneState(m, now, dayStart).state)).length,
    last15: sum((m) => m.calls.last_15_min),
    lastHour: sum((m) => m.calls.last_hour),
    today: sum((m) => m.calls.today),
    outgoing: sum((m) => m.calls.outgoing),
    incoming: sum((m) => m.calls.incoming),
    missed: sum((m) => m.calls.missed),
    connected: sum((m) => m.calls.connected),
    choices: sum((m) => m.calls.choices),
    answered: sum((m) => m.calls.answered_10s ?? 0),
    unrecorded: sum((m) => m.calls.unrecorded ?? 0),
    signinsToday: sum((m) => m.signins_today),
    withProblems: members.filter((m) => healthProblems(m, plan).length > 0).length,
    popupsShown: sum((m) => m.after_call?.shown_today ?? 0),
    popupChoices: sum((m) => m.after_call?.choices ?? 0),
    ringingIncoming: sum((m) => m.ringing?.incoming ?? 0),
    ringingWithPopup: sum((m) => m.ringing?.with_popup ?? 0),
  };
}
